import { RecommendationCard } from "./RecommendationCard";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Recommendation } from "@/types/artie-score";
import type { StickyNote } from "@/types/collaboration";
import { Sparkles, Clock, CheckCircle2 } from "lucide-react";

interface RecommendationsKanbanProps {
  recommendations: Recommendation[];
  projectId: string;
  onAccept: (recId: string) => void;
  onDecline: (recId: string) => void;
  onGoDeeper: (recId: string) => void;
  onComplete: (recId: string) => void;
  stickyNotes?: StickyNote[];
  onAddStickyNote?: (recId: string, content: string) => Promise<void>;
  onUpdateStickyNote?: (noteId: string, content: string) => Promise<void>;
  onDeleteStickyNote?: (noteId: string) => Promise<void>;
  canComment?: boolean;
  canEdit?: boolean;
  isOwner?: boolean;
  currentUserId?: string;
  readOnly?: boolean;
}

export const RecommendationsKanban = ({
  recommendations,
  projectId,
  onAccept,
  onDecline,
  onGoDeeper,
  onComplete,
  stickyNotes = [],
  onAddStickyNote,
  onUpdateStickyNote,
  onDeleteStickyNote,
  canComment = false,
  canEdit = false,
  isOwner = false,
  currentUserId = "",
  readOnly = false,
}: RecommendationsKanbanProps) => {
  // Group recommendations by status
  const activeRecs = recommendations.filter(r => 
    !r.status || r.status === 'generated' || r.status === 'presented'
  );
  const inProgressRecs = recommendations.filter(r => 
    r.status === 'accepted' || r.status === 'in_progress'
  );
  const completedRecs = recommendations.filter(r => r.status === 'done');

  const renderColumn = (
    title: string, 
    icon: React.ReactNode, 
    items: Recommendation[], 
    emptyMessage: string,
    colorClass: string
  ) => (
    <div className="flex flex-col h-full min-h-[500px] bg-secondary/20 rounded-xl border border-border/50 overflow-hidden">
      <div className={`p-4 border-b border-border/50 ${colorClass} bg-opacity-10 flex items-center gap-2 sticky top-0 bg-background/95 backdrop-blur z-10`}>
        {icon}
        <h3 className="font-semibold text-sm uppercase tracking-wide">{title}</h3>
        <span className="ml-auto bg-background text-muted-foreground text-xs py-0.5 px-2 rounded-full border border-border">
          {items.length}
        </span>
      </div>
      
      <ScrollArea className="flex-1 p-4">
        <div className="flex flex-col gap-4">
          {items.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm border-2 border-dashed border-border/50 rounded-lg">
              {emptyMessage}
            </div>
          ) : (
            items.map((rec, idx) => (
              <RecommendationCard
                key={idx}
                recommendation={rec}
                projectId={projectId}
                onAccept={onAccept}
                onDecline={onDecline}
                onGoDeeper={onGoDeeper}
                onComplete={onComplete}
                stickyNotes={stickyNotes}
                onAddStickyNote={onAddStickyNote ? (content) => onAddStickyNote(rec.id || rec.what_to_improve, content) : undefined}
                onUpdateStickyNote={onUpdateStickyNote}
                onDeleteStickyNote={onDeleteStickyNote}
                canComment={canComment}
                canEdit={canEdit}
                isOwner={isOwner}
                currentUserId={currentUserId}
                readOnly={readOnly}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full items-start">
      {/* To Do Column */}
      {renderColumn(
        "Opportunities",
        <Sparkles className="w-4 h-4 text-blue-500" />,
        activeRecs,
        "No new recommendations",
        "bg-blue-500/5 text-blue-700 dark:text-blue-400"
      )}

      {/* In Progress Column */}
      {renderColumn(
        "In Progress",
        <Clock className="w-4 h-4 text-yellow-500" />,
        inProgressRecs,
        "Nothing in progress",
        "bg-yellow-500/5 text-yellow-700 dark:text-yellow-400"
      )}

      {/* Done Column */}
      {renderColumn(
        "Completed",
        <CheckCircle2 className="w-4 h-4 text-green-500" />,
        completedRecs,
        "No completed items yet",
        "bg-green-500/5 text-green-700 dark:text-green-400"
      )}
    </div>
  );
};